/**
 * house-voice-copy.ts — Frontend copy map for House Voice
 *
 * Phase 1: ~8 key visible strings across guidebook, concierge, and TV surfaces.
 * Phase 2 will expand to full UI chrome (~30 strings).
 *
 * Each voice maps to variant strings for section headings, emergency body,
 * badges, TV hero lines, and greeting variants.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type HouseVoice =
  | "house_standard"
  | "attentive_host"
  | "english_butler"
  | "warm_local"
  | "minimalist"

export interface VoiceCopy {
  /** Guidebook — house rules section heading */
  gb_section_rules: string
  /** Guidebook — places section heading */
  gb_section_places: string
  /** Guidebook — emergency section heading */
  gb_section_emergency: string
  /** Guidebook — emergency section body text */
  gb_emergency_body: string
  /** Guidebook / Concierge — place badge label (replaces "Concierge pick") */
  gb_places_badge: string
  /** TV — hero stay line template. Use {property} and {day} as placeholders */
  tv_hero_stay_line: string
  /** TV — checkout nudge text */
  tv_hero_checkout_nudge: string
  /** Guidebook — afternoon greeting variant (morning/evening stay standard) */
  gb_welcome_greeting_afternoon: string
}

// ---------------------------------------------------------------------------
// Copy map
// ---------------------------------------------------------------------------

const COPY_MAP: Record<HouseVoice, VoiceCopy> = {
  house_standard: {
    gb_section_rules: "Good to know",
    gb_section_places: "Places we love",
    gb_section_emergency: "Need help?",
    gb_emergency_body: "We\u2019re always here for you. For anything at all, reach out directly to your host.",
    gb_places_badge: "Recommended",
    tv_hero_stay_line: "{property} is yours until {day}",
    tv_hero_checkout_nudge: "Safe travels!",
    gb_welcome_greeting_afternoon: "Good afternoon",
  },

  attentive_host: {
    gb_section_rules: "Good to know",
    gb_section_places: "Places we recommend",
    gb_section_emergency: "Need help?",
    gb_emergency_body: "We\u2019re here whenever you need us. For anything at all, just reach out \u2014 we\u2019re happy to help.",
    gb_places_badge: "Our recommendation",
    tv_hero_stay_line: "{property} is yours until {day}",
    tv_hero_checkout_nudge: "Have a wonderful trip home!",
    gb_welcome_greeting_afternoon: "Good afternoon",
  },

  english_butler: {
    gb_section_rules: "House guidelines",
    gb_section_places: "Recommended establishments",
    gb_section_emergency: "Assistance",
    gb_emergency_body: "Should anything be required, do not hesitate to contact your host directly.",
    gb_places_badge: "Recommended",
    tv_hero_stay_line: "{property} is at your disposal until {day}",
    tv_hero_checkout_nudge: "Safe onward travels.",
    gb_welcome_greeting_afternoon: "Good afternoon",
  },

  warm_local: {
    gb_section_rules: "A few things to know",
    gb_section_places: "Our favourite spots",
    gb_section_emergency: "Need anything?",
    gb_emergency_body: "We\u2019re here for you. Just reach out anytime \u2014 we\u2019re happy to help.",
    gb_places_badge: "Our pick",
    tv_hero_stay_line: "{property} is all yours until {day}",
    tv_hero_checkout_nudge: "Have a great trip home.",
    gb_welcome_greeting_afternoon: "Hey there",
  },

  minimalist: {
    gb_section_rules: "House rules",
    gb_section_places: "Places",
    gb_section_emergency: "Help",
    gb_emergency_body: "Contact your host directly for any assistance.",
    gb_places_badge: "Top pick",
    tv_hero_stay_line: "{property} until {day}",
    tv_hero_checkout_nudge: "Safe travels.",
    gb_welcome_greeting_afternoon: "Afternoon",
  },
}

// ---------------------------------------------------------------------------
// Accessor
// ---------------------------------------------------------------------------

/**
 * Resolve a voice key to its copy map.
 * Falls back to house_standard for invalid or missing keys.
 */
export function getVoiceCopy(voice: HouseVoice | string | undefined | null): VoiceCopy {
  const key = ((voice || "house_standard") as string).toLowerCase().trim() as HouseVoice
  return COPY_MAP[key] || COPY_MAP.house_standard
}

export { COPY_MAP }
